// What the Activities page shows of a distribution job: the table's order, the Top origins panel and the preview CSV.
// Arrivals may be summed across areas; people may not (one person can be in several), so people are never summed
// here. A sum only means something over every row, so a page must not call topOrigins on a cut preview.
// Pure: no React, router or network.
import type { DistributionRow } from '../services/activityDistributionsApi';
import type { Cell } from './csv';
import type { EeDistributionBand } from './eeActivities';

/** Most arrivals first; a row with no figure (withheld) goes last. */
export function sortByArrivals(rows: readonly DistributionRow[]): DistributionRow[] {
  return [...rows].sort((a, b) => {
    if (a.arrivals === null || b.arrivals === null) return a.arrivals === b.arrivals ? 0 : a.arrivals === null ? 1 : -1;
    return b.arrivals - a.arrivals;
  });
}

/** The time buckets of the rows, in order. */
export function bucketsOf(rows: readonly DistributionRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.time_bucket))).sort();
}

const arrivalsIn = (rows: readonly DistributionRow[], bucket: string): number =>
  rows.reduce((sum, r) => sum + (r.time_bucket === bucket ? r.arrivals ?? 0 : 0), 0);

/** The bucket with the most arrivals; null with no rows. */
export function busiestBucket(rows: readonly DistributionRow[]): string | null {
  return bucketsOf(rows).reduce<string | null>((best, b) => (best === null || arrivalsIn(rows, b) > arrivalsIn(rows, best) ? b : best), null);
}

/**
 * Where arrivals come from: the arrivals of each origin summed across the areas, biggest first, each with its share
 * of the arrivals that have a figure. An origin with no published arrivals is left out. Where an area has role "all"
 * rows only those count, as in the distributions (utils/eeDistributions.ts), so no arrival counts twice.
 */
export function topOrigins(rows: readonly DistributionRow[]): EeDistributionBand[] {
  const slot = (r: DistributionRow) => `${r.geo_id}|${r.time_bucket}`;
  const hasAll = new Set(rows.filter((r) => r.role === 'all').map(slot));
  const sums = new Map<string, { name: string; arrivals: number }>();
  for (const r of rows) {
    if (r.arrivals === null || (hasAll.has(slot(r)) && r.role !== 'all')) continue;
    const origin = sums.get(r.band) ?? { name: r.band_name ?? r.band, arrivals: 0 };
    origin.arrivals += r.arrivals;
    sums.set(r.band, origin);
  }
  const total = Array.from(sums.values()).reduce((sum, o) => sum + o.arrivals, 0);
  return Array.from(sums.entries())
    .map(([band, o]) => ({ band, name: o.name, min: null, max: null, arrivals: o.arrivals, people: null, share: total > 0 ? o.arrivals / total : null }))
    .sort((a, b) => b.arrivals - a.arrivals || a.name.localeCompare(b.name));
}

export const DISTRIBUTION_CSV_HEADERS = [
  'Area ID', 'Area name', 'Role', 'Distribution', 'Band', 'Band name', 'Band min', 'Band max', 'Unit', 'People', 'Arrivals', 'Time bucket (UTC)',
] as const;

/** The cells of a row in the order of DISTRIBUTION_CSV_HEADERS. A withheld figure is an empty cell. */
export function distributionCsvCells(r: DistributionRow): Cell[] {
  return [r.geo_id, r.geo_name, r.role, r.distribution, r.band, r.band_name, r.band_min, r.band_max, r.unit, r.people, r.arrivals, r.time_bucket];
}
